import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import { useAuth } from '../../context/AuthContext';
import './CompanyProfile.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5000/api';

const cleanArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) =>
      String(item || '').trim()
    )
    .filter(Boolean);
};

const createFormState = (profile = {}) => ({
  name: profile.name || '',
  company: profile.company || '',
  phone: profile.phone || '',
  position: profile.position || '',
  website: profile.website || '',
  location: profile.location || '',
  registrationNo:
    profile.registrationNo || '',
  yearsInBusiness:
    profile.yearsInBusiness || '',
  description:
    profile.description ||
    profile.bio ||
    '',
  serviceCategories: cleanArray(
    profile.serviceCategories
  ),
  ports: cleanArray(profile.ports),
  countries: cleanArray(profile.countries),
  certifications: cleanArray(
    profile.certifications
  )
});

const TagEditor = ({
  icon,
  label,
  values,
  inputValue,
  placeholder,
  onInputChange,
  onAdd,
  onRemove
}) => {
  const handleKeyDown = (event) => {
    if (
      event.key === 'Enter' ||
      event.key === ','
    ) {
      event.preventDefault();
      onAdd();
    }

    if (
      event.key === 'Backspace' &&
      !inputValue &&
      values.length
    ) {
      onRemove(
        values[values.length - 1]
      );
    }
  };

  return (
    <div className="vcp-tag-editor">
      <label>
        <i className={`fas ${icon}`}></i>
        {label}
      </label>

      <div className="vcp-tag-editor-box">
        {values.map((value) => (
          <span
            key={value}
            className="vcp-edit-tag"
          >
            {value}

            <button
              type="button"
              onClick={() =>
                onRemove(value)
              }
            >
              <i className="fas fa-times"></i>
            </button>
          </span>
        ))}

        <input
          type="text"
          value={inputValue}
          placeholder={
            values.length
              ? 'Add more...'
              : placeholder
          }
          onChange={(event) =>
            onInputChange(
              event.target.value
            )
          }
          onKeyDown={handleKeyDown}
        />

        <button
          type="button"
          className="vcp-tag-add"
          onClick={onAdd}
          disabled={!inputValue.trim()}
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>
    </div>
  );
};

const CompanyProfile = () => {
  const {
    token,
    updateProfile
  } = useAuth();

  const fileInputRef = useRef(null);

  const [profile, setProfile] =
    useState(null);

  const [formData, setFormData] =
    useState(createFormState());

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [isEditing, setIsEditing] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [messageType, setMessageType] =
    useState('');

  const [avatarFile, setAvatarFile] =
    useState(null);

  const [avatarPreview, setAvatarPreview] =
    useState('');

  const [avatarVersion, setAvatarVersion] =
    useState(Date.now());

  const [tagInputs, setTagInputs] =
    useState({
      serviceCategories: '',
      ports: '',
      countries: '',
      certifications: ''
    });

  const getToken = () =>
    token ||
    localStorage.getItem(
      'marilink_token'
    );

  const fetchProfile =
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setLoading(true);
        }

        const response = await fetch(
          `${API_BASE}/users/me`,
          {
            headers: {
              Authorization:
                `Bearer ${getToken()}`
            }
          }
        );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
              'Unable to load company profile.'
          );
        }

        setProfile(data.user);
        setFormData(
          createFormState(data.user)
        );

        setAvatarVersion(Date.now());
      } catch (error) {
        console.error(
          'Company profile error:',
          error
        );

        setProfile(null);

        showMessage(
          error.message ||
            'Unable to load profile.',
          'error'
        );
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    };

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const showMessage = (
    text,
    type = 'success'
  ) => {
    setMessage(text);
    setMessageType(type);

    window.setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleInputChange = (event) => {
    const { name, value } =
      event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value
    }));
  };

  const startEditing = () => {
    setFormData(
      createFormState(profile)
    );

    setTagInputs({
      serviceCategories: '',
      ports: '',
      countries: '',
      certifications: ''
    });

    setAvatarFile(null);
    setAvatarPreview('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setFormData(
      createFormState(profile)
    );

    setTagInputs({
      serviceCategories: '',
      ports: '',
      countries: '',
      certifications: ''
    });

    setAvatarFile(null);
    setAvatarPreview('');
    setIsEditing(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarButton = () => {
    if (!isEditing) {
      startEditing();
    }

    window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 0);
  };

  const handleAvatarChange = (event) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif'
    ];

    if (
      !allowedTypes.includes(file.type)
    ) {
      showMessage(
        'Please select a JPEG, PNG, WEBP or GIF image.',
        'error'
      );

      event.target.value = '';
      return;
    }

    if (
      file.size >
      15 * 1024 * 1024
    ) {
      showMessage(
        'Profile image must be smaller than 15 MB.',
        'error'
      );

      event.target.value = '';
      return;
    }

    setAvatarFile(file);

    const reader =
      new FileReader();

    reader.onload = () => {
      setAvatarPreview(
        reader.result
      );
    };

    reader.readAsDataURL(file);
  };

  const addTag = (field) => {
    const raw =
      tagInputs[field] || '';

    const value =
      raw.trim().replace(/,$/, '');

    if (!value) {
      return;
    }

    setFormData((previous) => {
      const current =
        cleanArray(previous[field]);

      const exists =
        current.some(
          (item) =>
            item.toLowerCase() ===
            value.toLowerCase()
        );

      if (exists) {
        return previous;
      }

      return {
        ...previous,
        [field]: [
          ...current,
          value
        ]
      };
    });

    setTagInputs((previous) => ({
      ...previous,
      [field]: ''
    }));
  };

  const removeTag = (
    field,
    value
  ) => {
    setFormData((previous) => ({
      ...previous,
      [field]: cleanArray(
        previous[field]
      ).filter(
        (item) => item !== value
      )
    }));
  };

  const buildListForSave = (
    field
  ) => {
    const list = cleanArray(
      formData[field]
    );

    const pending =
      (
        tagInputs[field] || ''
      ).trim();

    if (
      pending &&
      !list.some(
        (item) =>
          item.toLowerCase() ===
          pending.toLowerCase()
      )
    ) {
      list.push(pending);
    }

    return list;
  };

  const handleSave = async () => {
    if (
      !formData.company.trim()
    ) {
      showMessage(
        'Company name is required.',
        'error'
      );
      return;
    }

    try {
      setSaving(true);

      const payload =
        new FormData();

      payload.append(
        'name',
        formData.name.trim()
      );

      payload.append(
        'company',
        formData.company.trim()
      );

      payload.append(
        'phone',
        formData.phone.trim()
      );

      payload.append(
        'position',
        formData.position.trim()
      );

      payload.append(
        'website',
        formData.website.trim()
      );

      payload.append(
        'location',
        formData.location.trim()
      );

      payload.append(
        'registrationNo',
        formData.registrationNo.trim()
      );

      payload.append(
        'yearsInBusiness',
        formData.yearsInBusiness.trim()
      );

      payload.append(
        'bio',
        formData.description.trim()
      );

      payload.append(
        'description',
        formData.description.trim()
      );

      payload.append(
        'serviceCategories',
        JSON.stringify(
          buildListForSave(
            'serviceCategories'
          )
        )
      );

      payload.append(
        'ports',
        JSON.stringify(
          buildListForSave('ports')
        )
      );

      payload.append(
        'countries',
        JSON.stringify(
          buildListForSave(
            'countries'
          )
        )
      );

      payload.append(
        'certifications',
        JSON.stringify(
          buildListForSave(
            'certifications'
          )
        )
      );

      if (avatarFile) {
        payload.append(
          'avatarFile',
          avatarFile
        );
      }

      const response = await fetch(
        `${API_BASE}/users/profile`,
        {
          method: 'PUT',
          headers: {
            Authorization:
              `Bearer ${getToken()}`
          },
          body: payload
        }
      );

      const data =
        await response.json();

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            'Profile update failed.'
        );
      }

      let updatedUser =
        data.user;

      if (updateProfile) {
        try {
          const contextResult =
            await updateProfile({
              name:
                updatedUser.name ||
                '',
              company:
                updatedUser.company ||
                '',
              phone:
                updatedUser.phone ||
                '',
              position:
                updatedUser.position ||
                '',
              website:
                updatedUser.website ||
                '',
              location:
                updatedUser.location ||
                '',
              bio:
                updatedUser.bio ||
                updatedUser.description ||
                ''
            });

          if (
            contextResult?.success &&
            contextResult.user
          ) {
            updatedUser = {
              ...updatedUser,
              ...contextResult.user
            };
          }
        } catch (error) {
          console.warn(
            'Context profile sync failed:',
            error
          );
        }
      }

      setProfile(updatedUser);

      setFormData(
        createFormState(
          updatedUser
        )
      );

      setAvatarFile(null);
      setAvatarPreview('');

      if (
        fileInputRef.current
      ) {
        fileInputRef.current.value =
          '';
      }

      setAvatarVersion(Date.now());
      setIsEditing(false);

      showMessage(
        'Company profile updated successfully.',
        'success'
      );
    } catch (error) {
      console.error(
        'Profile save error:',
        error
      );

      showMessage(
        error.message ||
          'Unable to update profile.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const getAvatarUrl = () => {
    if (avatarPreview) {
      return avatarPreview;
    }

    if (
      profile?.avatar &&
      /^https?:\/\//i.test(
        profile.avatar
      )
    ) {
      return profile.avatar;
    }

    const id =
      profile?._id ||
      profile?.id;

    if (!id) {
      return '';
    }

    return `${API_BASE}/avatar/${id}?t=${avatarVersion}`;
  };

  const getInitials = () => {
    const value =
      profile?.company ||
      profile?.name ||
      'Company';

    return value
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) =>
        part.charAt(0).toUpperCase()
      )
      .join('');
  };

  const formatMemberSince = (
    value
  ) => {
    if (!value) {
      return 'N/A';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return 'N/A';
    }

    return date.toLocaleDateString(
      'en-US',
      {
        month: 'short',
        year: 'numeric'
      }
    );
  };

  const getWebsiteHref = (
    website
  ) => {
    if (!website) {
      return '';
    }

    if (
      /^https?:\/\//i.test(
        website
      )
    ) {
      return website;
    }

    return `https://${website}`;
  };

  const displayList = (
    list
  ) => {
    const values =
      cleanArray(list);

    return values.length
      ? values.join(', ')
      : 'Not set';
  };

  if (loading) {
    return (
      <div className="vcp-state">
        <span className="vcp-loader"></span>
        Loading company profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="vcp-state">
        <i className="fas fa-exclamation-circle"></i>
        Unable to load company profile.
      </div>
    );
  }

  const avatarUrl =
    getAvatarUrl();

  const companyName =
    profile.company ||
    profile.name ||
    'Company';

  const rating =
    Number(profile.rating) > 0
      ? Number(
          profile.rating
        ).toFixed(1)
      : 'N/A';

  const services =
    isEditing
      ? formData.serviceCategories
      : profile.serviceCategories;

  const ports =
    isEditing
      ? formData.ports
      : profile.ports;

  const countries =
    isEditing
      ? formData.countries
      : profile.countries;

  return (
    <div className="company-profile-page">
      <input
        ref={fileInputRef}
        className="vcp-hidden-file"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={
          handleAvatarChange
        }
      />

      <div className="vcp-page-head">
        <div>
          <h1>
            Company Profile
          </h1>

          <p>
            Company identity,
            coverage and contact
            information
          </p>
        </div>

        {!isEditing && (
          <button
            type="button"
            className="vcp-edit-btn"
            onClick={
              startEditing
            }
          >
            <i className="fas fa-pen"></i>
            Edit Profile
          </button>
        )}
      </div>

      {message && (
        <div
          className={`vcp-message ${messageType}`}
        >
          <i
            className={
              messageType ===
              'success'
                ? 'fas fa-check-circle'
                : 'fas fa-exclamation-circle'
            }
          ></i>

          {message}
        </div>
      )}

      <section className="vcp-summary-card">
        <div className="vcp-summary-main">
          <div className="vcp-avatar-wrap">
            <div className="vcp-avatar">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={companyName}
                  onError={(
                    event
                  ) => {
                    event.currentTarget.style.display =
                      'none';
                  }}
                />
              ) : (
                <span>
                  {getInitials()}
                </span>
              )}
            </div>

            <button
              type="button"
              className="vcp-avatar-change"
              title="Change profile picture"
              onClick={
                handleAvatarButton
              }
            >
              <i className="fas fa-camera"></i>
            </button>
          </div>

          <div className="vcp-identity">
            <div className="vcp-name-row">
              <h2>
                {companyName}
              </h2>

              <span
                className={`vcp-status ${
                  profile.isVerified
                    ? 'verified'
                    : 'pending'
                }`}
              >
                <i
                  className={
                    profile.isVerified
                      ? 'fas fa-check-circle'
                      : 'far fa-clock'
                  }
                ></i>

                {profile.isVerified
                  ? 'Verified'
                  : 'Pending'}
              </span>
            </div>

            <div className="vcp-identity-meta">
              {profile.location && (
                <span>
                  <i className="fas fa-map-marker-alt"></i>
                  {
                    profile.location
                  }
                </span>
              )}

              {profile.website && (
                <a
                  href={getWebsiteHref(
                    profile.website
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-globe"></i>
                  {
                    profile.website
                  }
                </a>
              )}
            </div>
          </div>

          <div className="vcp-stats">
            <div>
              <small>Rating</small>

              <strong>
                {rating}

                {rating !==
                  'N/A' && (
                  <i className="fas fa-star"></i>
                )}
              </strong>
            </div>

            <div>
              <small>
                Completed
              </small>

              <strong>
                {profile.completedJobs ||
                  0}
              </strong>
            </div>

            <div>
              <small>
                Member Since
              </small>

              <strong>
                {formatMemberSince(
                  profile.createdAt
                )}
              </strong>
            </div>
          </div>
        </div>

        <div className="vcp-coverage-bar">
          <div>
            <i className="fas fa-tools"></i>

            <span>
              <small>
                Services
              </small>

              <strong
                title={displayList(
                  services
                )}
              >
                {displayList(
                  services
                )}
              </strong>
            </span>
          </div>

          <div>
            <i className="fas fa-anchor"></i>

            <span>
              <small>Ports</small>

              <strong
                title={displayList(
                  ports
                )}
              >
                {displayList(
                  ports
                )}
              </strong>
            </span>
          </div>

          <div>
            <i className="fas fa-globe-americas"></i>

            <span>
              <small>
                Countries
              </small>

              <strong
                title={displayList(
                  countries
                )}
              >
                {displayList(
                  countries
                )}
              </strong>
            </span>
          </div>
        </div>
      </section>

      {!isEditing ? (
        <section className="vcp-details-card">
          <div className="vcp-section-head">
            <div>
              <i className="fas fa-info-circle"></i>
              <h3>
                Company Details
              </h3>
            </div>
          </div>

          <div className="vcp-detail-grid">
            <div className="vcp-detail">
              <small>Email</small>

              <strong>
                {profile.email ||
                  'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>Phone</small>

              <strong>
                {profile.phone ||
                  'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Contact Person
              </small>

              <strong>
                {profile.name ||
                  'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Position
              </small>

              <strong>
                {profile.position ||
                  'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Registration
              </small>

              <strong>
                {profile.registrationNo ||
                  'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Experience
              </small>

              <strong>
                {profile.yearsInBusiness
                  ? `${profile.yearsInBusiness} years`
                  : 'Not provided'}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Verification
              </small>

              <strong>
                {profile.verificationStatus ||
                  (profile.isVerified
                    ? 'Approved'
                    : 'Pending')}
              </strong>
            </div>

            <div className="vcp-detail">
              <small>
                Certifications
              </small>

              <strong
                title={displayList(
                  profile.certifications
                )}
              >
                {displayList(
                  profile.certifications
                )}
              </strong>
            </div>
          </div>

          {(profile.description ||
            profile.bio) && (
            <div className="vcp-about">
              <span>
                <i className="fas fa-align-left"></i>
                About
              </span>

              <p>
                {profile.description ||
                  profile.bio}
              </p>
            </div>
          )}
        </section>
      ) : (
        <section className="vcp-edit-card">
          <div className="vcp-section-head">
            <div>
              <i className="fas fa-pen"></i>
              <h3>
                Edit Company Profile
              </h3>
            </div>

            <span className="vcp-edit-note">
              Email and verification
              cannot be changed here
            </span>
          </div>

          <div className="vcp-edit-body">
            <div className="vcp-form-grid">
              <div className="vcp-field">
                <label>
                  Company Name
                </label>

                <input
                  type="text"
                  name="company"
                  value={
                    formData.company
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>
                  Contact Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={
                    formData.name
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>Phone</label>

                <input
                  type="text"
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>
                  Position
                </label>

                <input
                  type="text"
                  name="position"
                  value={
                    formData.position
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>
                  Website
                </label>

                <input
                  type="text"
                  name="website"
                  value={
                    formData.website
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="https://company.com"
                />
              </div>

              <div className="vcp-field">
                <label>
                  Location
                </label>

                <input
                  type="text"
                  name="location"
                  value={
                    formData.location
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>
                  Registration No.
                </label>

                <input
                  type="text"
                  name="registrationNo"
                  value={
                    formData.registrationNo
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>

              <div className="vcp-field">
                <label>
                  Years in Business
                </label>

                <input
                  type="number"
                  min="0"
                  name="yearsInBusiness"
                  value={
                    formData.yearsInBusiness
                  }
                  onChange={
                    handleInputChange
                  }
                />
              </div>
            </div>

            <div className="vcp-tag-grid">
              <TagEditor
                icon="fa-tools"
                label="Services"
                values={
                  formData.serviceCategories
                }
                inputValue={
                  tagInputs.serviceCategories
                }
                placeholder="Add service"
                onInputChange={(
                  value
                ) =>
                  setTagInputs(
                    (previous) => ({
                      ...previous,
                      serviceCategories:
                        value
                    })
                  )
                }
                onAdd={() =>
                  addTag(
                    'serviceCategories'
                  )
                }
                onRemove={(value) =>
                  removeTag(
                    'serviceCategories',
                    value
                  )
                }
              />

              <TagEditor
                icon="fa-anchor"
                label="Ports"
                values={
                  formData.ports
                }
                inputValue={
                  tagInputs.ports
                }
                placeholder="Add port"
                onInputChange={(
                  value
                ) =>
                  setTagInputs(
                    (previous) => ({
                      ...previous,
                      ports: value
                    })
                  )
                }
                onAdd={() =>
                  addTag('ports')
                }
                onRemove={(value) =>
                  removeTag(
                    'ports',
                    value
                  )
                }
              />

              <TagEditor
                icon="fa-globe-americas"
                label="Countries"
                values={
                  formData.countries
                }
                inputValue={
                  tagInputs.countries
                }
                placeholder="Add country"
                onInputChange={(
                  value
                ) =>
                  setTagInputs(
                    (previous) => ({
                      ...previous,
                      countries:
                        value
                    })
                  )
                }
                onAdd={() =>
                  addTag(
                    'countries'
                  )
                }
                onRemove={(value) =>
                  removeTag(
                    'countries',
                    value
                  )
                }
              />

              <TagEditor
                icon="fa-certificate"
                label="Certifications"
                values={
                  formData.certifications
                }
                inputValue={
                  tagInputs.certifications
                }
                placeholder="Add certification"
                onInputChange={(
                  value
                ) =>
                  setTagInputs(
                    (previous) => ({
                      ...previous,
                      certifications:
                        value
                    })
                  )
                }
                onAdd={() =>
                  addTag(
                    'certifications'
                  )
                }
                onRemove={(value) =>
                  removeTag(
                    'certifications',
                    value
                  )
                }
              />
            </div>

            <div className="vcp-field vcp-description-field">
              <label>
                Company Description
              </label>

              <textarea
                name="description"
                rows="3"
                value={
                  formData.description
                }
                onChange={
                  handleInputChange
                }
                placeholder="Brief company description"
              />
            </div>

            <div className="vcp-edit-actions">
              <button
                type="button"
                className="vcp-cancel-btn"
                disabled={saving}
                onClick={
                  cancelEditing
                }
              >
                Cancel
              </button>

              <button
                type="button"
                className="vcp-save-btn"
                disabled={saving}
                onClick={
                  handleSave
                }
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CompanyProfile;